import {
    AttackType,
    ComputedSetStats,
    FoodBonuses,
    FoodStatBonus,
    JobData,
    LevelStats,
    Mainstat,
    PartyBonusAmount,
    RawStatKey,
    RawStats
} from "./geartypes";
import {
    autoAttackModifier,
    autoCritBuffDmg,
    autoDhitBonusDmg,
    autoDhitBuffDmg,
    combineHasteTypes,
    critChance,
    critDmg,
    defIncomingDmg,
    detDmg,
    dhitChance,
    dhitDmg,
    fl,
    flp,
    mainStatMulti,
    mpTick,
    sksTickMulti,
    sksToGcd,
    spsTickMulti,
    spsToGcd,
    tenacityDmg,
    tenacityIncomingDmg,
    vitToHp,
    wdMulti
} from "./xivmath";
import {BASE_CP, BASE_GP, JobName, SupportedLevel} from "./xivconstants";
import {sum} from "@xivgear/util/array_utils";

/**
 * Adds the stats of 'addedStats' into 'baseStats'.
 *
 * Modifies 'baseStats' in-place.
 *
 * @param baseStats  The base stat sheet. Will be modified.
 * @param addedStats The stats to add.
 */
export function addStats(baseStats: RawStats, addedStats: Partial<RawStats>): void {
    for (const entry of Object.entries(baseStats)) {
        const stat = entry[0] as keyof RawStats;
        baseStats[stat] = (addedStats[stat] ?? 0) + (baseStats[stat] ?? 0);
    }
}

/**
 * Gets the base main stat, multiplied by the job stat modifier (typically 1.05) and
 * rounds it.
 *
 * @param levelStats the level stats
 * @param classJobStats the job data
 * @param stat which stat to get
 */
export function getBaseMainStat(levelStats: LevelStats, classJobStats: JobData, stat: Mainstat): number {
    return Math.floor(levelStats.baseMainStat * classJobStats.jobStatMultipliers[stat] / 100);
}

/**
 * Function from an attack type to a haste amount. Haste amount is percentage, e.g. 20 haste = 20% faster.
 */
export type HasteBonus = (attackType: AttackType) => number;

/**
 * Represents a post-computation stat modification (e.g. a crit chance buff).
 *
 * The modifier should modify the 'bonuses' object in-place.
 */
export type StatModification = (stats: ComputedSetStats, bonuses: RawBonusStats) => void;

/**
 * Represents a stat modification that is applied earlier in the process compared to {@link StatModification}.
 * This allows you to change the "base" values rather than merely applying final bonuses.
 */
export type StatPreModifications = {
    extraGearBonuses?: Partial<RawStats> | null,
    newFoodBonuses?: FoodBonuses | null,
}

/**
 * Represents post-computation stat modifications.
 */
export class RawBonusStats extends RawStats {
    critChance: number = 0;
    critDmg: number = 0;
    dhitChance: number = 0;
    dhitDmg: number = 0;
    detMulti: number = 0;
    traitHaste: HasteBonus[] = [];
    // TODO: These should be used when possible
    forceCrit: boolean = false;
    forceDh: boolean = false;
    // note that **not** all haste that seems to come from a gauge is actually gauge haste. this is specifically
    // for Paeon at this time.
    gaugeHaste: number = 0;
}

function clamp(min: number, max: number, value: number) {
    return Math.max(Math.min(value, max), min);
}

/**
 * ComputedSetStats implementation.
 *
 * Unlike the old ComputedSetStats, this should not be modified. Rather, if a modified version is required,
 * then the {@link withModifications()} method should be used, which will apply the allowable modifications and
 * return a new object. Derived values do not need to be explicitly recomputed, e.g. if you apply a main stat bonus,
 * the main stat multiplier will take effect automatically.
 */
export class ComputedSetStatsImpl implements ComputedSetStats {

    protected readonly finalBonusStats: RawBonusStats;
    // This is initialized when the ctor calls this.recalc()
    private currentStats!: RawStats;
    private _effectiveFoodBonuses!: RawStats;
    private _effectiveMedicineBonuses!: RawStats;

    constructor(
        readonly gearStats: RawStats,
        private readonly foodStats: FoodBonuses,
        readonly level: SupportedLevel,
        readonly levelStats: LevelStats,
        private readonly classJob: JobName,
        private readonly classJobStats: JobData,
        readonly partyBonus: PartyBonusAmount,
        readonly racialStats: RawStats,
        private readonly medicineStats: FoodBonuses = {}
    ) {
        this.finalBonusStats = new RawBonusStats();
        this._effectiveMedicineBonuses = new RawStats();
        // TODO: order of operations here
        this.recalc();
        if (classJobStats.traits) {
            classJobStats.traits.forEach(trait => {
                if (trait.minLevel && trait.minLevel > level) {
                    return;
                }
                if (trait.maxLevel && trait.maxLevel < level) {
                    return;
                }
                trait.apply(this.finalBonusStats);
            });
        }
        for (const key in this.medicineStats) {
            const stat = key as RawStatKey;
            const bonus = this.medicineStats[stat]!;
            const effective = Math.min(fl(this[stat] * (bonus.percentage / 100)), bonus.max);
            this.finalBonusStats[stat] += effective;
            this._effectiveMedicineBonuses[stat] += effective;
        }
    }

    private recalc() {
        const finalized = finalizeStatsInt(
            this.gearStats,
            this.foodStats,
            this.level,
            this.levelStats,
            this.classJob,
            this.classJobStats,
            this.partyBonus
        );
        this.currentStats = finalized.raw;
        this._effectiveFoodBonuses = finalized.effectiveFoodBonuses;
    }

    withModifications(modifications: StatModification, pre: StatPreModifications = {}): ComputedSetStatsImpl {
        let gearStats = this.gearStats;
        if (pre.extraGearBonuses) {
            gearStats = {...gearStats};
            addStats(gearStats, pre.extraGearBonuses);
        }
        const out = new ComputedSetStatsImpl(
            gearStats,
            // If the new food is not specified, use current food.
            // If the new food is null, use empty food.
            // If the new food is not null, use its bonuses.
            'newFoodBonuses' in pre ? (pre.newFoodBonuses ?? {}) : this.foodStats,
            this.level,
            this.levelStats,
            this.classJob,
            this.classJobStats,
            this.partyBonus,
            this.racialStats,
            this.medicineStats
        );
        Object.assign(out.finalBonusStats, this.finalBonusStats);
        modifications(out, out.finalBonusStats);
        return out;
    }

    gcdPhys(baseGcd: number, haste?: number): number {
        return sksToGcd(baseGcd, this.levelStats, this.skillspeed, haste);
    }

    gcdMag(baseGcd: number, haste?: number): number {
        return spsToGcd(baseGcd, this.levelStats, this.spellspeed, haste);
    }

    haste(attackType: AttackType, buffHaste: number, gaugeHaste: number): number {
        const traitHaste = sum(this.finalBonusStats.traitHaste.map(hb => hb(attackType)));
        return combineHasteTypes(buffHaste, this.gearHaste, traitHaste, this.finalBonusStats.gaugeHaste);
    }

    effectiveAaDelay(buffHaste: number, gaugeHaste: number): number {
        const effectiveHaste = this.haste('Auto-attack', buffHaste, gaugeHaste);
        return flp(3, this.aaDelay * (100 - effectiveHaste) / 100);
    }

    traitMulti(attackType: AttackType): number {
        return this.classJobStats.traitMulti?.(this.level, attackType) ?? 1.0;
    }

    get hp(): number {
        return this.currentStats.hp + this.finalBonusStats.hp + vitToHp(this.levelStats, this.classJobStats, this.vitality);
    }

    get vitality(): number {
        return this.currentStats.vitality + this.finalBonusStats.vitality;
    }

    get strength(): number {
        return this.currentStats.strength + this.finalBonusStats.strength;
    }

    get dexterity(): number {
        return this.currentStats.dexterity + this.finalBonusStats.dexterity;
    }

    get intelligence(): number {
        return this.currentStats.intelligence + this.finalBonusStats.intelligence;
    }

    get mind(): number {
        return this.currentStats.mind + this.finalBonusStats.mind;
    }

    get piety(): number {
        return this.currentStats.piety + this.finalBonusStats.piety;
    }

    get crit(): number {
        return this.currentStats.crit + this.finalBonusStats.crit;
    }

    get dhit(): number {
        return this.currentStats.dhit + this.finalBonusStats.dhit;
    }

    get determination(): number {
        return this.currentStats.determination + this.finalBonusStats.determination;
    }

    get tenacity(): number {
        return this.currentStats.tenacity + this.finalBonusStats.tenacity;
    }

    get skillspeed(): number {
        return this.currentStats.skillspeed + this.finalBonusStats.skillspeed;
    }

    get spellspeed(): number {
        return this.currentStats.spellspeed + this.finalBonusStats.spellspeed;
    }

    get wdPhys(): number {
        return this.currentStats.wdPhys + this.finalBonusStats.wdPhys;
    }

    get wdMag(): number {
        return this.currentStats.wdMag + this.finalBonusStats.wdMag;
    }

    get defensePhys(): number {
        return this.currentStats.defensePhys + this.finalBonusStats.defensePhys;
    }

    get defenseMag(): number {
        return this.currentStats.defenseMag + this.finalBonusStats.defenseMag;
    }

    get gearHaste(): number {
        return this.currentStats.gearHaste + this.finalBonusStats.gearHaste;
    }

    get weaponDelay(): number {
        const result = this.currentStats.weaponDelay + this.finalBonusStats.weaponDelay;
        if (result <= 0) {
            // Return large value since that's better than trying to divide by zero
            return 100_000;
        }
        return result;
    }

    get craftsmanship(): number {
        return this.currentStats.craftsmanship + this.finalBonusStats.craftsmanship;
    }

    get control(): number {
        return this.currentStats.control + this.finalBonusStats.control;
    }

    get cp(): number {
        return BASE_CP + this.currentStats.cp + this.finalBonusStats.cp;
    }

    get perception(): number {
        return this.currentStats.perception + this.finalBonusStats.perception;
    }

    get gathering(): number {
        return this.currentStats.gathering + this.finalBonusStats.gathering;
    }

    get gp(): number {
        return BASE_GP + this.currentStats.gp + this.finalBonusStats.gp;
    }

    get job(): JobName {
        return this.classJob;
    }

    get jobStats(): JobData {
        return this.classJobStats;
    }

    get baseCritChance(): number {
        return clamp(0, 1, critChance(this.levelStats, this.crit));
    }

    get critChance(): number {
        if (this.finalBonusStats.forceCrit) {
            return 1;
        }
        return clamp(0, 1, this.baseCritChance + this.finalBonusStats.critChance);
    }

    get critMulti(): number {
        return critDmg(this.levelStats, this.crit) + this.finalBonusStats.critDmg;
    };

    get baseDhitChance(): number {
        return clamp(0, 1, dhitChance(this.levelStats, this.dhit));
    }

    get dhitChance(): number {
        if (this.finalBonusStats.forceDh) {
            return 1;
        }
        return clamp(0, 1, this.baseDhitChance + this.finalBonusStats.dhitChance);
    };

    get dhitMulti(): number {
        return dhitDmg(this.levelStats, this.dhit) + this.finalBonusStats.dhitDmg;
    };

    get detMulti(): number {
        return detDmg(this.levelStats, this.determination) + this.finalBonusStats.detMulti;
    };

    get spsDotMulti(): number {
        return spsTickMulti(this.levelStats, this.spellspeed);
    };

    get sksDotMulti(): number {
        return sksTickMulti(this.levelStats, this.skillspeed);
    };

    get tncMulti(): number {
        return tenacityDmg(this.levelStats, this.tenacity);
    };

    get tncIncomingMulti(): number {
        return tenacityIncomingDmg(this.levelStats, this.tenacity);
    };

    get wdMulti(): number {
        const wdEffective = Math.max(this.wdMag, this.wdPhys);
        return wdMulti(this.levelStats, this.classJobStats, wdEffective);
    };

    get mainStatValue(): number {
        if (!this.classJobStats.mainStat) {
            return 0;
        }
        return this[this.classJobStats.mainStat];
    }

    get mainStatMulti(): number {
        return mainStatMulti(this.levelStats, this.classJobStats, this.mainStatValue);
    };

    get baseMainStatPlusRace(): number {
        if (!this.classJobStats.mainStat) {
            return 0;
        }
        const mainStat = this.classJobStats.mainStat;
        return getBaseMainStat(this.levelStats, this.classJobStats, this.classJobStats.mainStat) + this.racialStats[mainStat];
    }

    get aaStatMulti(): number {
        if (!this.classJobStats.autoAttackStat) {
            return 0;
        }
        return mainStatMulti(this.levelStats, this.classJobStats, this[this.classJobStats.autoAttackStat]);
    };

    get wdMultiPetAction(): number {
        const wdEffective = Math.max(this.wdMag, this.wdPhys);
        return wdMulti(this.levelStats, this.classJobStats, wdEffective, true);
    };

    get autoDhBonus(): number {
        return autoDhitBonusDmg(this.levelStats, this.dhit);
    };

    get autoCritBuffMulti(): number {
        return autoCritBuffDmg(this.critMulti, this.finalBonusStats.critChance);
    }

    get autoDhitBuffMulti(): number {
        return autoDhitBuffDmg(this.dhitMulti, this.finalBonusStats.dhitChance);
    }

    get mpPerTick(): number {
        return mpTick(this.levelStats, this.piety);
    };

    get aaMulti(): number {
        return autoAttackModifier(this.levelStats, this.classJobStats, this.weaponDelay, this.wdPhys);
    };

    get aaDelay(): number {
        return this.weaponDelay;
    };

    get defenseDamageTaken(): number {
        return defIncomingDmg(this.levelStats, this.defensePhys);
    };

    get magicDefenseDamageTaken(): number {
        return defIncomingDmg(this.levelStats, this.defenseMag);
    }

    get effectiveFoodBonuses(): RawStats {
        return this._effectiveFoodBonuses;
    }

    get effectiveMedicineBonuses(): RawStats {
        return this._effectiveMedicineBonuses;
    }

    get extraMainStat(): 0 {
        // Always 0 at this point because the extra main stat is already factored into the actual main stat.
        return 0;
    }

    get extraSecondaryStat(): 0 {
        // Always 0 at this point because the extra secondary stat is already factored into the actual secondary stat.
        return 0;
    }
}

export function finalizeStats(
    gearStats: RawStats,
    foodStats: FoodBonuses,
    level: SupportedLevel,
    levelStats: LevelStats,
    classJob: JobName,
    classJobStats: JobData,
    partyBonus: PartyBonusAmount,
    racialStats: RawStats,
    medicineStats: FoodBonuses = {}
) {
    return new ComputedSetStatsImpl(
        gearStats, foodStats, level, levelStats, classJob, classJobStats, partyBonus, racialStats, medicineStats
    );

}

export function finalizeStatsInt(
    // TODO: gearStats is currently gear + race/job base stuff. Separate these out.
    gearStats: RawStats,
    foodStats: FoodBonuses,
    level: SupportedLevel,
    levelStats: LevelStats,
    classJob: JobName,
    classJobStats: JobData,
    partyBonus: PartyBonusAmount,
    medicineStats: FoodBonuses = {}
): {
    raw: RawStats,
    effectiveFoodBonuses: RawStats,
    effectiveMedicineBonuses: RawStats
} {
    const combinedStats: RawStats = {...gearStats};
    if (classJobStats.type === 'Combat') {
        const mainStatKey = classJobStats.mainStat;
        const aaStatKey = classJobStats.autoAttackStat;
        combinedStats[mainStatKey] = fl(combinedStats[mainStatKey] * (1 + 0.01 * partyBonus));
        if (aaStatKey && mainStatKey !== aaStatKey) {
            combinedStats[aaStatKey] = fl(combinedStats[aaStatKey] * (1 + 0.01 * partyBonus));
        }
    }
    combinedStats.vitality = fl(combinedStats.vitality * (1 + 0.01 * partyBonus));
    const effectiveFoodBonuses = new RawStats();
    const effectiveMedicineBonuses = new RawStats();
    // Food stats
    for (const key in foodStats) {
        const stat = key as RawStatKey;
        // These operate on base values, without the party buff
        const bonus: FoodStatBonus | undefined = foodStats[stat];
        if (bonus !== undefined) {
            const startingValue = gearStats[stat];
            const extraValue = Math.min(bonus.max, Math.floor(startingValue * (bonus.percentage / 100)));
            combinedStats[stat] += extraValue;
            effectiveFoodBonuses[stat] += extraValue;
        }
    }
    for (const key in medicineStats) {
        const stat = key as RawStatKey;
        const bonus = medicineStats[stat];
        if (bonus !== undefined) {
            const extraValue = Math.min(bonus.max, fl(combinedStats[stat] * (bonus.percentage / 100)));
            combinedStats[stat] += extraValue;
            effectiveMedicineBonuses[stat] += extraValue;
        }
    }
    return {
        raw: combinedStats,
        effectiveFoodBonuses: effectiveFoodBonuses,
        effectiveMedicineBonuses: effectiveMedicineBonuses,
    };
}
